// FAR-72: turn the engine's planning-board rows into the grid's rows.
//
// GET /board returns the engine's nested PlanningBoardRow:
//   { materialNumber, productionLine, briteCode, dosMin, dosMax, percentOfMinInv,
//     runRateCasesPerHour, hasAnyIssue, months: [{ month: "2026-09", dos,
//     forecast, scheduledCases, requiredHours, isScenarioModified, ... }] }
// The grid wants one flat row per item with per-month fields keyed by a month
// key ("sep26_cases"). Nothing converted between them, so real rows rendered
// blank and the board fell back to demo rows. This is the one place that
// conversion happens; everything else (grid columns, line summaries,
// modifications) reads the month list it produces.

const MONTH_ABBR = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const MONTH_NAME = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// monthKey("2026-09") -> "sep26": the grid field prefix for that month.
export function monthKey(iso) {
  const [y, m] = String(iso).split('-').map(Number)
  if (!y || !m || m < 1 || m > 12) return null
  return `${MONTH_ABBR[m - 1]}${String(y).slice(-2)}`
}

// monthLabel("2026-09") -> "Sep 2026".
export function monthLabel(iso) {
  const [y, m] = String(iso).split('-').map(Number)
  if (!y || !m || m < 1 || m > 12) return String(iso)
  return `${MONTH_NAME[m - 1]} ${y}`
}

// boardMonths(rows) -> [{ key: "sep26", iso: "2026-09", label: "Sep 2026" }, ...]
// sorted by date: the union of every row's months. Columns come from this,
// not a hardcoded list (the old list was Apr-Sep 2026; the real plan runs
// Sep 2026 - Aug 2027).
export function boardMonths(engineRows) {
  const set = new Set()
  for (const r of engineRows || []) {
    for (const m of r.months || []) if (monthKey(m.month)) set.add(m.month)
  }
  return [...set].sort().map((iso) => ({ key: monthKey(iso), iso, label: monthLabel(iso) }))
}

const round1 = (n) => Math.round(n * 10) / 10

// toGridRow(engineRow) -> the flat row the grid renders.
export function toGridRow(r) {
  const row = {
    itemNumber: r.materialNumber,
    description: r.description || '',
    line: r.productionLine || '',
    briteCode: r.briteCode || '',
    dosMin: r.dosMin,
    dosMax: r.dosMax,
    // FAR-76: shown as "Min · Target · Max"; Measure; lot rounding.
    dosTarget: r.dosTarget || 0,
    baseUom: r.baseUom || '',
    roundingValue: r.roundingValue || 0,
    isDosPlanned: !!r.isDosPlanned,
    // FAR-71: made to order. Demand shows; inventory and DOS are not projected.
    isMadeToOrder: !!r.isMadeToOrder,
    // FAR-67: this item's demand includes other branch warehouses, and/or
    // another plant also makes it. Read-only: the numbers are unchanged.
    hasOtherWarehouseDemand: !!r.hasOtherWarehouseDemand,
    warehouseDemand: r.warehouseDemand || null,
    alsoMadeAt: r.alsoMadeAt || null,
    percentOfMinInv: r.percentOfMinInv,
    runRateCasesPerHour: r.runRateCasesPerHour || 0,
    packSize: r.packSize || 0,
    beginningInv: r.beginningInventory || 0,
    hasIssue: !!r.hasAnyIssue,
  }
  for (const m of r.months || []) {
    const k = monthKey(m.month)
    if (!k) continue
    // notProjected: rows stored before FAR-83, whose made-to-order months had
    // no projection. Newer rows project them (0 while units match demand).
    row[`${k}_dos`] = m.notProjected ? null : m.dos // null renders "—"
    row[`${k}_fcst`] = m.forecast
    row[`${k}_cases`] = m.scheduledCases
    row[`${k}_hrs`] = round1(m.requiredHours || 0)
    row[`${k}_endInv`] = m.notProjected ? null : m.endingInventory
    row[`${k}_scenarioModified`] = !!m.isScenarioModified
    if (m.isCellEdited) row[`${k}_cellEdited`] = true // FAR-76: planner typed it
    // FAR-84 §4.1: no demand ahead -> DOS 0 by rule, but nothing is short.
    if (m.noDemandAhead) row[`${k}_noDemand`] = true
    // FAR-79: a typed month flagged after a Base Plan switch.
    if (m.needsReview) {
      row[`${k}_needsReview`] = true
      row[`${k}_reviewReason`] = m.reviewReason || ''
    }
    if (m.basePlanUnits != null) row[`${k}_basePlanUnits`] = m.basePlanUnits
    if (m.hasIssue) row[`${k}_issue`] = m.issueType || true
  }
  return row
}

// Required hours for a given number of cases, from the item's own run rate
// (engine-supplied). 0 when the item has no run rate -- never a made-up one.
export function hoursFor(cases, runRateCasesPerHour) {
  if (!runRateCasesPerHour || runRateCasesPerHour <= 0) return 0
  return round1((cases || 0) / runRateCasesPerHour)
}
